/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___   ____  ____   ___  
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _| |  _ \|  _ \ / _ \ 
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |  | |_) | |_) | | | |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |  |  __/|  _ <| |_| |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___| |_|   |_| \_\\___/ 
                                                                                                                                                                                                                                                                                                                                       
=========================================================
* Horizon UI Dashboard PRO - v1.0.0
=========================================================

* Product Page: https://www.horizon-ui.com/pro/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
// Chakra imports
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  FormErrorMessage,
  useColorModeValue,
  Text,
  useToast,
} from '@chakra-ui/react';
import { FormEvent, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

// Custom components
import CenteredAuth from 'layouts/auth/variants/Centered';
import authRepository from 'api/authRepository';

function ForgotPassword() {
  const toast = useToast();
  const navigate = useNavigate();
  const textColor = useColorModeValue('#2D2A5E', 'white');
  const textColorSecondary = 'gray.400';
  const brandStars = useColorModeValue('#7062B9', '#A89CE8');
  const inputBg = useColorModeValue('#EEEBFA', '#1B254B');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setEmailError('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setEmailError('Enter a valid email address.');
      return;
    }

    setEmailError('');
    setIsSubmitting(true);
    try {
      await authRepository.forgotPassword(normalizedEmail);
      toast({
        title: 'Password reset email sent',
        description: 'Check your inbox for the reset link or token.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      navigate('/auth/new-password');
    } catch (error: any) {
      console.error('Unable to request password reset:', error);
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : '') ||
        error?.message ||
        'Unable to send the password reset email.';
      toast({
        title: 'Password reset request failed',
        description: message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CenteredAuth
      cardTop={{ base: '140px', md: '24vh' }}
      cardBottom={{ base: '50px', lg: 'auto' }}
    >
      <Flex
        w="100%"
        maxW="max-content"
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        px={{ base: '25px', md: '0px' }}
        flexDirection="column"
      >
        <Box me="auto" mb="34px">
          <Heading
            color={textColor}
            fontSize={{ base: '3xl', md: '36px' }}
            mb="16px"
          >
            Forgot your password?
          </Heading>
          <Text
            color={textColorSecondary}
            fontSize="md"
            w={{ base: '100%', lg: '456px' }}
            maxW="100%"
          >
            No problem. Just let us know your email address and we'll email you
            a password reset link that will allow you to choose a new one.
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', lg: '456px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
          align="start"
        >
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <FormControl isInvalid={!!emailError}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired={true}
              variant="auth"
              bg={inputBg}
              color={textColor}
              borderColor="transparent"
              borderRadius="14px"
              fontSize="sm"
              type="email"
              placeholder="you@email.com"
              mb="24px"
              size="lg"
              h="52px"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (emailError) setEmailError('');
              }}
              autoComplete="email"
              _focus={{
                borderColor: '#7062B9',
                boxShadow: '0 0 0 1px #7062B9',
              }}
            />
            <FormErrorMessage>{emailError}</FormErrorMessage>
            <Button
              type="submit"
              fontSize="sm"
              bg="#7062B9"
              color="white"
              borderRadius="14px"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              isLoading={isSubmitting}
              _hover={{ bg: '#5B4FA3' }}
            >
              Email password reset link
            </Button>
          </FormControl>
          </form>
          <RouterLink to="/auth/sign-in">
            <Text color={brandStars} fontSize="sm" fontWeight="600">
              Back to sign in
            </Text>
          </RouterLink>
        </Flex>
      </Flex>
    </CenteredAuth>
  );
}

export default ForgotPassword;
